import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { Box, Grid } from '@material-ui/core';
import { catalogProductsFlatSelector } from 'state/catalog/selectors';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { Form } from 'forms/types';
import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { isUIEnabledSelector } from 'state/app/selectors';
import { LargeButton, LargeButtonColor } from 'components/LargeButton/LargeButton';
import { makeStyles } from '@material-ui/core/styles';
import { number, string } from 'yup';
import { NumberControlField } from 'components/NumberControl/NumberControl';
import { PromoDeliveryType, PromoDiscountType, PromoLabel } from 'types';
import { PromoDiscountItemField } from './PromoDiscountItemField';
import { PromoLogoControlField } from './PromoLogoField';
import { PromoOptionGroupField, PromoOptionGroupFieldProps } from './PromoOptionGroupField';
import { Text, TextTypography } from 'components/Text/Text';
import { TextAreaControlField } from 'components/TextAreaControl/TextAreaControl';
import { useFormError, validator } from 'core/form/form';
import { useSelector } from 'react-redux';
import i18n from '../../../i18n';
import { VendorPromoSubmitRequest } from 'state/promo/types';
import { getCurrencyText } from '../../core/utils/utils';
import css from './PromoForm.module.css';

interface PromoFormOwnProps {
    isEditable?: boolean;
    onClose: () => void;
}

const useStyles = makeStyles(theme => ({
    marginContainer: {},
    combinedContainer: {
        maxWidth: 250,
        flexBasis: '100%',
        '&:first-child': {
            marginRight: theme.spacing(1),
        },
        '&:last-child': {
            marginLeft: theme.spacing(1),
        },
        [theme.breakpoints.down(375)]: {
            '&:first-child': {
                marginRight: 0,
            },
            '&:last-child': {
                marginTop: theme.spacing(2),
                marginLeft: 0,
            },
        },
    },
    textContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    aloneItem: {
        margin: '0 !important',
        flexBasis: '50%',
    },
    formSection: {
        color: '#243143',
    },
    descriptionBlock: {
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    optionGroupBlock: {
        '& span': {
            paddingTop: 0,
            paddingBottom: 0,
            [theme.breakpoints.down(480)]: {
                marginTop: 4,
                marginBottom: 4,
            },
        },
        marginBottom: theme.spacing(3),
    },
    discountBlock: {
        display: 'flex',
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down(375)]: {
            display: 'block',
            justifyContent: 'column',
            width: '100%',
            maxWidth: 245,
        },
    },
    imageHint: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(5),
    },
    deliveryTypeHint: {
        color: '#243143',
        paddingTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
    footerWrapper: {
        paddingRight: 16,
    },
    conditionContainer: {
        display: 'flex',
    },
    formContainer: {
        flex: 1,
    },
    minOrderPriceContainer: {
        width: '50%',
        marginRight: theme.spacing(3),
        [theme.breakpoints.down(640)]: {
            width: '100%',
            maxWidth: 245,
            marginRight: 0,
        },
    },
    resultHint: {
        margin: theme.spacing(2, 0),
    },
    label: {
        display: 'flex',
        color: '#243143',
        textAlign: 'left',
    },
    priceContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2.5),
        [theme.breakpoints.down(640)]: {
            flexDirection: 'column',
        },
    },
    priceItem: {
        width: '50%',
        color: '#243143',
        [theme.breakpoints.down(640)]: {
            width: '100%',
            maxWidth: 245,
            marginTop: theme.spacing(1),
        },
    },
    line: {
        margin: theme.spacing(3, 0),
        [theme.breakpoints.down(481)]: {
            margin: theme.spacing(2, 0),
        },
    },
    footer: {
        borderTop: `1px solid ${theme.palette.brandPrimary.accent}`,
        width: '87%',
        position: 'absolute',
        bottom: 0,
        left: 40,
        background: '#FDFFFC',
        zIndex: 2,
        [theme.breakpoints.down(640)]: {
            width: '92%',
            left: 24,
        },
        [theme.breakpoints.down(480)]: {
            width: '94%',
            left: 16,
        },
        [theme.breakpoints.down(376)]: {
            width: '90%',
        },
    },
    footerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        marginTop: '0 auto',
        paddingTop: 16,
        marginBottom: 16,
    },
    footerItem: {
        width: '50%',
        '&:first-child': {
            marginRight: 16,
        },
    },
}));

export const promoDeliveryTypeItems: PromoOptionGroupFieldProps = {
    labelMarker: 'common:enums.PROMO_DELIVERY_TYPE',
    values: Object.entries(PromoDeliveryType).reduce((acc, [_, tes]) => [...acc, tes], []),
};

export const promoDiscountTypeItems: PromoOptionGroupFieldProps = {
    labelMarker: 'common:enums.PROMO_DISCOUNT_TYPE',
    values: Object.entries(PromoDiscountType).reduce((acc, [_, tes]) => [...acc, tes], []),
};

const formSelector = formValueSelector(Form.PROMO_FORM);
const discountTypeSelector = <T extends {}>(state: T) => formSelector(state, 'discountType');

export const PromoForm = reduxForm<unknown, PromoFormOwnProps>({
    form: Form.PROMO_FORM,
    initialValues: {
        minOrderPrice: 0,
        deliveryType: PromoDeliveryType.ANY,
        discountType: PromoDiscountType.ORDER,
        discount: null as number,
        discountedItemId: null,
        description: '',
        image: PromoLabel.GIFT,
        active: false,
    },
    validate: validator({
        minOrderPrice: number()
            .nullable()
            .transform(value => (isNaN(value) ? undefined : value))
            .required('common:validation.required')
            .min(0, 'vendor:promos.form.minOrderPriceError'),
        deliveryType: string().required('common:validation.required'),
        discountType: string().required('common:validation.required'),
        discount: number()
            .nullable()
            .transform(value => (isNaN(value) ? undefined : value))
            .required('common:validation.required')
            .min(1, 'vendor:promos.form.minDiscountError')
            .max(99, 'vendor:promos.form.maxOrderDiscountError'),
        discountItem: number()
            .nullable()
            .transform(value => (isNaN(value) ? undefined : value))
            .required('common:validation.required')
            .min(1, 'vendor:promos.form.minDiscountError')
            .max(100, 'vendor:promos.form.maxDiscountError'),
        description: string().required('common:validation.required'),
    }),
})(props => {
    const { handleSubmit, initialValues, onClose } = props;
    const { t } = i18n.useTranslation();
    const error = useFormError(Form.PROMO_FORM);
    const classes = useStyles();

    const isUIEnabled = useSelector(isUIEnabledSelector);
    const vendorProducts = useSelector(catalogProductsFlatSelector);
    const discountType = useSelector(discountTypeSelector);

    const [promoDiscountType, setPromoDiscountType] = useState(
        initialValues && (initialValues as VendorPromoSubmitRequest).discountType,
    );

    const handleChangePromoDiscountType = useCallback((value: any) => setPromoDiscountType(value), [
        setPromoDiscountType,
    ]);
    const validateDiscountedItem = useCallback(
        (v: string) => {
            if (discountType === PromoDiscountType.PRODUCT && !v) {
                return t('common:validation.required');
            }
        },
        [discountType, t],
    );

    return (
        <form className={classes.formContainer} onSubmit={handleSubmit}>
            <div className={classes.marginContainer}>
                <div className={classes.formSection}>
                    <Text typography={TextTypography.TITLE_ACCENT_CHANGE} className={classes.label}>
                        {t('vendor:promos.form.сonditions')}
                    </Text>
                </div>
                <div className={classes.priceContainer}>
                    <div className={classes.minOrderPriceContainer}>
                        <Field
                            name="minOrderPrice"
                            component={NumberControlField}
                            min={0}
                            step={0.001}
                            label={t('vendor:promos.form.minOrderPrice') + getCurrencyText()}
                        />
                    </div>
                    <div className={classes.priceItem}>
                        <Text typography={TextTypography.CAPTION}>{t('vendor:promos.form.conditionsInfo')}</Text>
                    </div>
                </div>
                <div className={classes.deliveryTypeHint}>
                    <Text typography={TextTypography.CAPTION}>{t('vendor:promos.form.deliveryTypeHint')}</Text>
                </div>
                <div className={classes.optionGroupBlock}>
                    <Field
                        name="deliveryType"
                        isEditable={props.isEditable}
                        component={PromoOptionGroupField}
                        labelMarker={promoDeliveryTypeItems.labelMarker}
                        values={promoDeliveryTypeItems.values}
                    />
                </div>
                <div className={classes.line}>
                    <HorizontalDivider />
                </div>
                <div className={classes.formSection}>
                    <Text typography={TextTypography.TITLE_ACCENT_CHANGE}>{t('vendor:promos.form.result')}</Text>
                </div>
                <div className={classes.resultHint}>
                    <Text typography={TextTypography.PARA}>{t('vendor:promos.form.resultHint')}</Text>
                </div>
                <div className={classes.optionGroupBlock}>
                    <Field
                        name="discountType"
                        isEditable={props.isEditable}
                        component={PromoOptionGroupField}
                        labelMarker={promoDiscountTypeItems.labelMarker}
                        values={promoDiscountTypeItems.values}
                        onChange={handleChangePromoDiscountType}
                    />
                </div>

                {promoDiscountType && promoDiscountType === PromoDiscountType.PRODUCT ? (
                    <div className={classes.discountBlock}>
                        <div className={classes.combinedContainer}>
                            <Field
                                name="discountedItemId"
                                component={PromoDiscountItemField}
                                products={vendorProducts}
                                validate={validateDiscountedItem}
                                label={t('vendor:promos.form.discountItem')}
                            />
                        </div>
                        <div className={classes.combinedContainer}>
                            <Field
                                name="discount"
                                component={NumberControlField}
                                label={t('vendor:promos.form.discount')}
                                min={0}
                                max={100}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={classes.discountBlock}>
                        <div className={classNames(classes.combinedContainer, classes.aloneItem)}>
                            <Field
                                name="discount"
                                component={NumberControlField}
                                label={t('vendor:promos.form.discount')}
                                min={0}
                                max={99}
                            />
                        </div>
                    </div>
                )}
                <div className={classes.line}>
                    <HorizontalDivider />
                </div>
                <div className={classes.formSection}>
                    <Text typography={TextTypography.TITLE_ACCENT_CHANGE}>{t('vendor:promos.form.description')}</Text>
                </div>
                <div className={classes.resultHint}>
                    <Text typography={TextTypography.PARA}>{t('vendor:promos.form.descriptionHint')}</Text>
                </div>
                <div className={classes.descriptionBlock}>
                    <Field
                        name="description"
                        component={TextAreaControlField}
                        maxLength={80}
                        label={t('vendor:promos.form.description')}
                    />
                </div>
                <div className={classes.line}>
                    <HorizontalDivider />
                </div>
                <div>
                    <Text typography={TextTypography.PARA}>{t('vendor:promos.form.imageHint')}</Text>
                </div>
                <div className={classes.imageHint}>
                    <Field name="image" component={PromoLogoControlField} />
                </div>
                {error && (
                    <Grid item xs={12}>
                        <Box m={2}>
                            <FormErrorMessage error={error} />
                        </Box>
                    </Grid>
                )}
                <div className={classes.line}>
                    <HorizontalDivider />
                </div>
            </div>

            <footer className={classes.footer}>
                <div className={classes.footerContainer}>
                    <div className={classes.footerItem}>
                        <LargeButton onClick={onClose} color={LargeButtonColor.GRAY}>
                            <div>
                                <Text typography={TextTypography.PARA_ACCENT}>{t('onboarding:card.cancel')}</Text>
                            </div>
                        </LargeButton>
                    </div>
                    <div className={classes.footerItem}>
                        <LargeButton disabled={!isUIEnabled} type="submit">
                            <div>
                                <Text typography={TextTypography.PARA_ACCENT} className={css.text_color_white}>
                                    {t(props.isEditable ? 'onboarding:card.save' : 'onboarding:card.add')}
                                </Text>
                            </div>
                        </LargeButton>
                    </div>
                </div>
            </footer>
        </form>
    );
});
